import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import { useResponsive } from '../../hooks/useResponsive';
import { useRealTimeMetrics } from '../../hooks/useRealTimeMetrics';
import { useDateRange } from '../../hooks/useDateRange';
import { trackReportExport } from '../../services/analytics';
import { FilterSidebar } from '../FilterPanel';
import { Download, ChevronDown } from 'lucide-react';

export default function AttractionsView() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();
  const responsive = useResponsive();
  const { days, label: dateLabel } = useDateRange();
  const [attractions, setAttractions] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [popularityRange, setPopularityRange] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(!responsive.isSmall);

  const { data, isLoading } = useRealTimeMetrics({
    endpoint: 'attractions',
    interval: 30000,
    days,
  });

  // Extract unique categories from attractions for filter options
  const categoryOptions = useMemo(() => {
    const categories = new Map<string, number>();
    (data?.topPOIs || []).forEach((poi: any) => {
      const cat = poi.category || 'Uncategorized';
      categories.set(cat, (categories.get(cat) || 0) + 1);
    });
    return Array.from(categories.entries()).map(([label, count]) => ({
      label,
      value: label,
      count,
    }));
  }, [data?.topPOIs]);

  // Memoize sorted and filtered attractions
  const filteredAttractions = useMemo(() => {
    let filtered = data?.topPOIs || [];

    // Apply category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((poi: any) => selectedCategories.includes(poi.category || 'Uncategorized'));
    }

    // Apply popularity filter (OR logic - match ANY selected range)
    if (popularityRange.length > 0) {
      filtered = filtered.filter((poi: any) => {
        const interactions = poi.interactions || 0;
        return popularityRange.some(range => {
          if (range === 'high') return interactions >= 100;
          if (range === 'medium') return interactions >= 50 && interactions < 100;
          if (range === 'low') return interactions < 50;
          return false;
        });
      });
    }

    return filtered
      .sort((a: any, b: any) => (b.interactions || 0) - (a.interactions || 0))
      .slice(0, 100);
  }, [data?.topPOIs, selectedCategories, popularityRange]);

  useEffect(() => {
    setAttractions(filteredAttractions);
  }, [filteredAttractions]);

  const handleExport = useCallback(() => {
    const tenantId = user?.role === 'superadmin' ? selectedTenantId : user?.tenantId;
    if (!tenantId) return;
    const token = localStorage.getItem('access_token');
    const url = `/api/dashboard/export/attractions?tenantId=${tenantId}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', '');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.click();
        URL.revokeObjectURL(objectUrl);
        trackReportExport('csv', 'attractions');
      });
  }, [user, selectedTenantId]);

  const handleResetFilters = useCallback(() => {
    setSelectedCategories([]);
    setPopularityRange([]);
  }, []);

  const filters = {
    categories: {
      title: 'Categories',
      options: categoryOptions,
      selectedValues: selectedCategories,
    },
    popularity: {
      title: 'Popularity',
      options: [
        { label: 'High (100+)', value: 'high', count: (data?.topPOIs || []).filter((p: any) => (p.interactions || 0) >= 100).length },
        { label: 'Medium (50-99)', value: 'medium', count: (data?.topPOIs || []).filter((p: any) => {
          const i = p.interactions || 0;
          return i >= 50 && i < 100;
        }).length },
        { label: 'Low (<50)', value: 'low', count: (data?.topPOIs || []).filter((p: any) => (p.interactions || 0) < 50).length },
      ],
      selectedValues: popularityRange,
    },
  };

  if (user?.role === 'superadmin' && !selectedTenantId) {
    return (
      <div className="p-6 bg-red-50 rounded-xl border border-red-300 text-red-900">
        Please select a tenant from the dropdown above to view data.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 m-0">Top Attractions</h1>
          <p className="text-sm text-slate-500 mt-1">Most visited locations · {dateLabel}</p>
        </div>
        <button
          onClick={handleExport}
          disabled={attractions.length === 0}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 bg-white text-gray-700 text-xs font-medium transition-all ${
            attractions.length > 0
              ? 'hover:bg-slate-50 cursor-pointer opacity-100'
              : 'cursor-not-allowed opacity-50'
          }`}
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      <div className={`grid gap-6 ${responsive.isSmall ? 'grid-cols-1' : 'grid-cols-[280px_1fr]'}`}>
        {/* Filter Sidebar - Hidden on mobile unless filterOpen */}
        <div className={responsive.isSmall && !filterOpen ? 'hidden' : 'block'}>
          {responsive.isSmall && (
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="w-full px-4 py-3 mb-4 bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 hover:bg-slate-200 transition-colors"
            >
              Filters
              <ChevronDown size={16} className={`transition-transform duration-200 ${filterOpen ? 'rotate-180' : 'rotate-0'}`} />
            </button>
          )}
          {filterOpen && (
            <FilterSidebar
              filters={filters}
              onFilterChange={(key, values) => {
                if (key === 'categories') setSelectedCategories(values);
                if (key === 'popularity') setPopularityRange(values);
              }}
              onReset={handleResetFilters}
            />
          )}
        </div>

        <div>
          {isLoading && !attractions.length ? (
            <div className="p-6 text-center text-slate-500 bg-white rounded-xl border border-slate-200">
              <div className="inline-block w-6 h-6 border-2 border-slate-200 border-t-blue-700 rounded-full animate-spin" />
              <p className="mt-3">Loading attractions...</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {attractions.length > 0 ? (
                <>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-100">
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Rank</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Interactions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attractions.map((a: any, idx: number) => (
                        <tr key={a.id || idx} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                          <td className="px-4 py-3 text-slate-500 font-medium">#{idx + 1}</td>
                          <td className="px-4 py-3"><span className="font-medium text-slate-900">{a.name}</span></td>
                          <td className="px-4 py-3"><span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">{a.category || 'Uncategorized'}</span></td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold text-blue-700">
                              {a.interactions?.toLocaleString() || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isLoading && <p className="m-2 text-xs text-slate-400">⟳ Updating...</p>}
                </>
              ) : (
                <div className="text-center py-10 text-slate-400">
                  No attractions match the selected filters
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
